import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_FIELD_FLOWS } from "@src/util/query/fieldFlow.query";

/**
 * Extract the comparison value from a triggerValue.
 * Lookup fields store {id, label} JSON — we compare by id.
 * All other fields store the raw value.
 */
function getCompareValue(triggerValue) {
    if (!triggerValue) return "";
    try {
        const p = JSON.parse(triggerValue);
        if (p?.id) return p.id;
    } catch {}
    return triggerValue;
}

function evaluateCondition(operator, currentValue, triggerValue) {
    const compare = getCompareValue(triggerValue);
    const curr = currentValue ?? "";

    switch (operator) {
        case "equals":
            return String(curr) === String(compare);
        case "notEquals":
            return String(curr) !== String(compare);
        case "contains":
            return String(curr).toLowerCase().includes(String(compare).toLowerCase());
        case "notEmpty":
            return Array.isArray(curr)
                ? curr.length > 0
                : curr !== "" && curr != null;
        case "isEmpty":
            return Array.isArray(curr)
                ? curr.length === 0
                : curr === "" || curr == null;
        case "greaterThan":
            return Number(curr) > Number(compare);
        case "lessThan":
            return Number(curr) < Number(compare);
        default:
            return false;
    }
}

/**
 * Returns { hiddenPropertyIds, conditionallyRequiredPropertyIds } — two Sets of property _ids:
 *  - hiddenPropertyIds: should be HIDDEN because their reveal condition is not currently satisfied.
 *  - conditionallyRequiredPropertyIds: should be treated as MANDATORY right now because a Field
 *    Flow's condition that requires them is currently satisfied (requiredPropertyIds is always a
 *    subset of that same flow's revealedPropertyIds — see fieldFlows.jsx's builder UI). Union this
 *    with a property's own static isMandatory flag when deciding whether a field can be left empty.
 *
 * Works with two property structures:
 *  - ObjectQuery items  (AgencyObjectQuery etc.): { _id: assignmentId, propertyId: propertyId, propertyDetail: { label } }
 *  - GROUPLIST items    (profile tabs):            { _id: propertyId, label }
 *
 * Works with two formData structures:
 *  - Form drawer format: [{ fieldName: value }, ...]  e.g. [{ suppliername: "Acme" }]
 *  - Detail page format: [{ name: "suppliername", value: "Acme" }, ...]
 *  - Flat record object: { suppliername: "Acme", ... }  (pass directly, will be converted)
 *
 * Also returns the raw `flows` array (the same data GET_FIELD_FLOWS resolves) so callers that
 * need the trigger→revealed relationships themselves — e.g. to nest a dependent field under its
 * parent and lock its position, as ModuleFormBuilder/DraggableList and the detail-page profile
 * tabs both do — don't need a second query.
 *
 * Usage:
 *   const { hiddenPropertyIds, conditionallyRequiredPropertyIds, flows } = useFieldFlowVisibility("Agency", properties, formData);
 *   // Then filter your render list:
 *   list.filter(p => !hiddenPropertyIds.has(String(p?.propertyId ?? p?._id)))
 *
 * @param {string} moduleType  - e.g. "Agency", "Branch", "Employee"
 * @param {Array}  properties  - schema array (ObjectQuery or GROUPLIST items)
 * @param {Array|Object} formData - current form values (see formats above)
 */
export function useFieldFlowVisibility(moduleType, properties, formData) {
    const { data } = useQuery(GET_FIELD_FLOWS, {
        variables: { moduleType },
        skip: !moduleType,
        fetchPolicy: "cache-and-network",
    });

    const flows = data?.getFieldFlows?.data ?? [];

    // Normalize formData to a {labelKey: value} plain object for fast lookup
    const valueByLabel = useMemo(() => {
        // Plain object — already in {labelKey: value} format
        if (formData && !Array.isArray(formData)) {
            return formData;
        }
        const map = {};
        (formData ?? []).forEach((entry) => {
            if (entry && typeof entry === "object") {
                // Detail page format: { name: "fieldname", value: "val" }
                if ("name" in entry && "value" in entry) {
                    map[entry.name] = entry.value;
                } else {
                    // Form drawer format: { fieldname: value }
                    const key = Object.keys(entry)[0];
                    if (key) map[key] = entry[key];
                }
            }
        });
        return map;
    }, [formData]);

    // Build map: propertyId (string) -> current form value
    // Handles both ObjectQuery items (propertyId + propertyDetail.label)
    // and GROUPLIST items (_id + label directly)
    const valueByPropId = useMemo(() => {
        const map = {};
        (properties ?? []).forEach((prop) => {
            // Property's own _id: prefer propertyId (ObjectQuery), fall back to _id (GROUPLIST)
            const propId = String(prop?.propertyId ?? prop?._id ?? "");
            // Label key: prefer propertyDetail.label (ObjectQuery), fall back to label (GROUPLIST)
            const labelKey = (prop?.propertyDetail?.label ?? prop?.label ?? "")
                .replaceAll(" ", "").toLowerCase();
            if (propId && labelKey) {
                map[propId] = valueByLabel[labelKey];
            }
        });
        return map;
    }, [properties, valueByLabel]);

    // Which revealed property IDs currently satisfy their trigger condition, and — of those —
    // which are also required by that same flow (requiredPropertyIds ⊆ revealedPropertyIds)
    const { visibleRevealedIds, conditionallyRequiredPropertyIds } = useMemo(() => {
        const visible = new Set();
        const required = new Set();
        flows.forEach((flow) => {
            const currentValue = valueByPropId[String(flow.triggerPropertyId)];
            if (evaluateCondition(flow.operator, currentValue, flow.triggerValue)) {
                (flow.revealedPropertyIds ?? []).forEach((id) => visible.add(String(id)));
                (flow.requiredPropertyIds ?? []).forEach((id) => required.add(String(id)));
            }
        });
        return { visibleRevealedIds: visible, conditionallyRequiredPropertyIds: required };
    }, [flows, valueByPropId]);

    // All property IDs that appear in any flow's revealedPropertyIds (conditional fields)
    const allConditionalIds = useMemo(() => {
        const ids = new Set();
        flows.forEach((flow) => {
            (flow.revealedPropertyIds ?? []).forEach((id) => ids.add(String(id)));
        });
        return ids;
    }, [flows]);

    // Hidden = is conditional AND its condition is not currently met
    const hiddenPropertyIds = useMemo(() => {
        const hidden = new Set();
        allConditionalIds.forEach((id) => {
            if (!visibleRevealedIds.has(id)) hidden.add(id);
        });
        return hidden;
    }, [allConditionalIds, visibleRevealedIds]);

    return { hiddenPropertyIds, conditionallyRequiredPropertyIds, flows };
}
